/**
 * Which notification channel, if any, the citizen is currently verified on.
 *
 * PRO-635. A surface that offers reminders must ask this before showing the
 * VERIFICATION screen: the original bug was a screen that never looked at the
 * session, so navigating away and back always dropped a verified citizen in
 * front of "enter your mobile number" — indistinguishable from being logged
 * out. The session was fine the whole time: the cookie lives an hour, matching
 * the backend's VERIFICATION_CODE_VERIFIED_EXPIRATION.
 *
 * Kept as a pure function of the cookies so the decision can be tested without
 * standing up a router.
 */
import { getCookie } from 'src/utils/cookies';
import {
  emailCodeCookie,
  emailNotificationProperty,
  emailServiceAddress,
  smsCodeCookie,
  smsNotificationProperty,
  smsServicePhoneNumber,
} from 'src/consts/cookie-consts';

export type TVerifiedChannel = 'sms' | 'email' | null;

/** A channel that is actually verified — never `null`. */
export type TVerifiedChannelName = Exclude<TVerifiedChannel, null>;

const CHANNELS: TVerifiedChannelName[] = ['sms', 'email'];

const codeCookieFor = (channel: TVerifiedChannelName) =>
  channel === 'sms' ? smsCodeCookie : emailCodeCookie;

const propertyCookieFor = (channel: TVerifiedChannelName) =>
  channel === 'sms' ? smsNotificationProperty : emailNotificationProperty;

/**
 * Is this channel verified for THIS address?
 *
 * The property check is the point. These cookies are scoped to the portal
 * domain, so a verification made while managing one address used to apply to
 * whatever address was chosen next: the screen said "You're already verified as
 * +45 …" for an address that contact had no relationship with, and on a shared
 * device it showed one citizen's phone number to another. Verifying proves you
 * own a phone number; it says nothing about which address you may manage.
 *
 * Scoping at the read rather than clearing on address change covers every route
 * that switches address — several places write `AUTH_PROPERTY_ID`, and a
 * mismatch check cannot be forgotten in a new one.
 */
export const isChannelVerified = (
  channel: TVerifiedChannel,
  propertyId: string | undefined
): boolean => {
  if (!channel || !propertyId) return false;
  return (
    Boolean(getCookie(codeCookieFor(channel))) &&
    getCookie(propertyCookieFor(channel)) === propertyId
  );
};

/**
 * Every channel verified for this address — both, one, or none.
 *
 * This replaced a `getVerifiedChannel` that returned a single channel and
 * preferred SMS when both were live. That arbitrary preference hid the other
 * session completely: verify by phone, then by email, and the portal kept
 * showing the phone number with no hint that the email session existed or any
 * way to reach it. Both are equally valid, so both are the citizen's to choose
 * between.
 */
export const getVerifiedChannels = (
  propertyId: string | undefined
): TVerifiedChannelName[] =>
  CHANNELS.filter((channel) => isChannelVerified(channel, propertyId));

/**
 * The phone number or address the session belongs to, for showing the citizen
 * whose settings these are. Read from the cookies rather than the store: the
 * store's `other.email` is never seeded from `emailServiceAddress` on a cold
 * load, so email mode would show a blank.
 */
export const getVerifiedContact = (channel: TVerifiedChannel): string => {
  if (channel === 'sms') return getCookie(smsServicePhoneNumber);
  if (channel === 'email') return getCookie(emailServiceAddress);
  return '';
};

/** Spacing and case are how a citizen types, not what they mean. */
const normalizeContact = (value: string) =>
  value.replace(/\s+/g, '').toLowerCase();

/**
 * Is this the contact the citizen is already verified on?
 *
 * Typing the number you are already using should not start the whole
 * verification dance over — you would wait for an SMS, retype a code, and end
 * up exactly where you already were. Worse, the portal said nothing at all
 * about it, so it looked like the form had simply ignored you.
 */
export const isCurrentContact = (
  channel: TVerifiedChannel,
  value: string | undefined
): boolean => {
  const current = getVerifiedContact(channel);
  if (!current || !value) return false;
  return normalizeContact(current) === normalizeContact(value);
};
